/**
 * EmployeeAccordionPlan.tsx
 * The F1 accordion body of the Employee Schedule L1 redesign (2026-07-25): the
 * person's whole visible window as FLAT task rows — no WO group lines, no
 * bottom totals row (the person's own row carries the summary):
 *
 *   [nickname chip] Task name     ─ bar ─  ─ bar ─ …
 *
 * • Chip = derived project nickname (P3), coloured per project; full label +
 *   WO#/quote# in the tooltip ("people call them projects").
 * • Bars sit on the 7-day grid (Sat-anchored weeks); contiguous same-% week
 *   rows merge into one run. Runs ≤2 days print their % OUTSIDE the bar in
 *   ink (inside labels don't fit at 20px/day) — flipping left at the window's
 *   right edge.
 * • Click a bar → the assignment drawer. This view is READ + inspect only —
 *   the round-12 feedback removed drag-to-extend here (v1.1.43); date changes
 *   go through the drawer / Scenario Planning.
 */

import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LOAD_NAME_W, LOAD_WEEK_W } from './EmployeeWeekLoadRow';
import { assignmentPrimaryLabel, addDaysISO, dayIndexInWeek } from './employeeScheduleShared';
import { deriveProjectNickname, nicknameColorIndex } from '../../utils/projectNickname';
import type { EmployeeBar } from '../../services/scheduleEmployeeBars';

const ROW_H = 26;

// ── plan model: bars → project groups → task runs ───────────────────────────

interface TaskRun {
  startDate: string;
  endDate: string;
  percent: number;
  tentative: boolean;
  /** Last week-row of the run — what a bar click hands to the drawer. */
  lastBar: EmployeeBar;
}

interface PlanRow {
  key: string;
  taskName: string;
  projectLabel: string;
  nickname: string;
  colorIdx: number;
  isAbsence: boolean;
  runs: TaskRun[];
}

function buildPlanRows(bars: readonly EmployeeBar[]): PlanRow[] {
  const rows = new Map<string, PlanRow>();
  const sorted = [...bars].sort((a, b) => a.startDate.localeCompare(b.startDate) || a.name.localeCompare(b.name));
  for (const b of sorted) {
    const isAbsence = b.kind !== 'Task';
    const projKey = isAbsence ? 'absence' : (b.workOrderKey ?? b.quoteKey ?? b.projectKey ?? `task:${b.name}`);
    const rowKey = `${projKey}::${b.name}`;
    let row = rows.get(rowKey);
    if (!row) {
      const projectLabel = isAbsence ? 'Time off' : assignmentPrimaryLabel(b);
      row = {
        key: rowKey,
        taskName: b.name,
        projectLabel,
        nickname: isAbsence ? 'Time off' : deriveProjectNickname(b.workOrderName?.trim() || b.projectName?.trim() || b.name),
        colorIdx: nicknameColorIndex(projKey, 6),
        isAbsence,
        runs: [],
      };
      rows.set(rowKey, row);
    }
    const prev = row.runs[row.runs.length - 1];
    if (prev && addDaysISO(prev.endDate, 1) === b.startDate && prev.percent === b.percent && prev.tentative === (b.status === 'Tentative')) {
      prev.endDate = b.endDate;
      prev.lastBar = b;
    } else {
      row.runs.push({ startDate: b.startDate, endDate: b.endDate, percent: b.percent, tentative: b.status === 'Tentative', lastBar: b });
    }
  }
  // Stable order: by first run start, absences last.
  return [...rows.values()].sort((a, b) =>
    Number(a.isAbsence) - Number(b.isAbsence) ||
    (a.runs[0]?.startDate ?? '').localeCompare(b.runs[0]?.startDate ?? ''));
}

// ── component ───────────────────────────────────────────────────────────────

/**
 * A synthetic row rendered ABOVE the derived plan (Scenario Phase-3 S1: the
 * "this job" slate slice, which lives only in the draft and so is absent from
 * the employee-bars payload). Purely presentational — no click-through.
 */
export interface PinnedPlanRow {
  nickname: string;
  taskName: string;
  /** Bar + chip colour (Phase 3 passes theme.schedule.machineTime — "this project"). */
  color: string;
  /** Tentative styling: dashed outline + translucent fill (a draft's own slice). */
  tentative?: boolean;
  runs: Array<{ startDate: string; endDate: string; percent: number; tooltip?: string }>;
}

export interface EmployeeAccordionPlanProps {
  bars: EmployeeBar[];
  weekStarts: string[];
  /** Bar click-through (Employee Schedule opens the assignment drawer). Omit
   *  for inspect-only hosts (Scenario Phase 3) — bars keep their tooltips. */
  onBarClick?: (bar: EmployeeBar, anchor: HTMLElement) => void;
  /** Label-gutter width — defaults to the Employee Schedule's 300px. */
  nameWidth?: number;
  /**
   * Per-week column widths for hosts with NON-uniform columns (Phase 3: wide
   * project weeks + narrow outlook weeks). Defaults to uniform LOAD_WEEK_W.
   * Must be the same length as `weekStarts` when provided.
   */
  weekWidths?: number[];
  /** Synthetic rows rendered above the plan (e.g. the draft's own slice). */
  pinnedRows?: PinnedPlanRow[];
}

export const EmployeeAccordionPlan: React.FC<EmployeeAccordionPlanProps> = ({
  bars, weekStarts, onBarClick, nameWidth = LOAD_NAME_W, weekWidths, pinnedRows,
}) => {
  const theme = useTheme();
  const s = theme.schedule;
  const chipPalette = [s.machineTime, s.employeeAlloc, '#8A6D3B', '#6B5B7A', s.weekendWork, '#996B4D'];
  const rows = useMemo(() => buildPlanRows(bars), [bars]);
  const windowStart = weekStarts[0] ?? '';
  const totalDays = weekStarts.length * 7;

  // Column geometry — cumulative offsets support non-uniform week widths.
  const widths = useMemo(
    () => (weekWidths && weekWidths.length === weekStarts.length ? weekWidths : weekStarts.map(() => LOAD_WEEK_W)),
    [weekWidths, weekStarts],
  );
  const offsets = useMemo(() => {
    const out: number[] = [0];
    for (const w of widths) out.push(out[out.length - 1] + w);
    return out;
  }, [widths]);
  const trackW = offsets[offsets.length - 1];
  /** Left px of a global day index (0 = window-start Saturday). */
  const dayX = (idx: number): number => {
    const w = Math.min(widths.length - 1, Math.floor(idx / 7));
    return offsets[w] + (idx - w * 7) * (widths[w] / 7);
  };

  const barGeometry = (startDate: string, endDate: string) => {
    const startIdx = Math.max(0, dayIndexInWeek(startDate, windowStart));
    const endIdx = Math.min(totalDays - 1, dayIndexInWeek(endDate, windowStart));
    if (endIdx < startIdx) return { left: 0, width: 0 };
    const left = dayX(startIdx) + 1;
    const endW = Math.min(widths.length - 1, Math.floor(endIdx / 7));
    const right = dayX(endIdx) + widths[endW] / 7 - 1;
    return { left, width: right - left };
  };

  /** One plan line — shared by pinned and derived rows. */
  const renderLine = (opts: {
    key: string; nickname: string; taskName: string; chipColor: string; title: string;
    divider: boolean;
    runs: Array<{ key: string; left: number; width: number; percent: number; color: string; dashed?: boolean; tooltip: string; onClick?: (e: React.MouseEvent<HTMLElement>) => void }>;
  }) => (
    <Box key={opts.key} sx={{ display: 'flex', alignItems: 'stretch', height: ROW_H, borderTop: opts.divider ? `1px solid ${theme.schedule.selectionBg}` : 'none', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.03) } }}>
      <Box
        title={opts.title}
        sx={{ width: nameWidth, flexShrink: 0, position: 'sticky', left: 0, zIndex: 2, backgroundColor: theme.palette.background.paper, display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, minWidth: 0, borderRight: `1px solid ${theme.palette.divider}` }}
      >
        <Box component="span" sx={{ flexShrink: 0, fontSize: '0.56rem', fontWeight: 800, color: theme.palette.common.white, borderRadius: '4px', px: 0.85, py: '1px', letterSpacing: 0.2, maxWidth: 96, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', backgroundColor: opts.chipColor }}>
          {opts.nickname}
        </Box>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opts.taskName}</Typography>
      </Box>
      <Box sx={{ position: 'relative', width: trackW, flexShrink: 0 }}>
        {/* week seams */}
        {weekStarts.map((ws, wi) => (
          <Box key={ws} sx={{ position: 'absolute', top: 0, bottom: 0, left: offsets[wi], width: '1px', backgroundColor: alpha(theme.palette.divider, 0.5) }} />
        ))}
        {opts.runs.map((run) => {
          if (run.width <= 0) return null;
          const labelInside = run.width >= 38;
          return (
            <React.Fragment key={run.key}>
              <Box
                onClick={run.onClick}
                title={run.tooltip}
                sx={{
                  position: 'absolute', top: '50%', mt: '-7px', height: 14, left: run.left, width: run.width,
                  borderRadius: '7px', color: theme.palette.common.white,
                  fontSize: '0.56rem', fontWeight: 800, display: 'flex', alignItems: 'center',
                  justifyContent: labelInside ? 'flex-end' : 'center', pr: labelInside ? '6px' : 0,
                  boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.18)}`,
                  cursor: run.onClick ? 'pointer' : 'default',
                  // Tentative (a draft's own slice): dashed outline + translucent fill.
                  ...(run.dashed
                    ? { backgroundColor: alpha(run.color, 0.55), border: `1.5px dashed ${run.color}` }
                    : { backgroundColor: run.color }),
                }}
              >
                {labelInside ? `${run.percent}%` : ''}
              </Box>
              {!labelInside && (
                <Typography
                  component="span"
                  sx={{
                    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                    fontSize: '0.56rem', fontWeight: 800, color: theme.palette.text.primary,
                    whiteSpace: 'nowrap', pointerEvents: 'none',
                    ...(run.left + run.width > trackW - 40
                      ? { right: trackW - run.left + 4 }
                      : { left: run.left + run.width + 4 }),
                  }}
                >
                  {run.percent}%
                </Typography>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: theme.palette.background.paper, borderBottom: `2px solid ${alpha(theme.schedule.committed, 0.4)}` }}>
      {(pinnedRows ?? []).map((p, pi) => renderLine({
        key: `pin-${pi}`, nickname: p.nickname, taskName: p.taskName, chipColor: p.color,
        title: `${p.nickname} — ${p.taskName}`, divider: false,
        runs: p.runs.map((r, ri) => {
          const g = barGeometry(r.startDate, r.endDate);
          return { key: `pin-${pi}-${ri}`, left: g.left, width: g.width, percent: r.percent, color: p.color, dashed: p.tentative,
            tooltip: r.tooltip ?? `${p.taskName} · ${r.startDate} – ${r.endDate} · ${r.percent}% of their day` };
        }),
      }))}
      {rows.length === 0 && (pinnedRows ?? []).length === 0 && (
        <Typography sx={{ px: 2, py: 1.25, fontSize: '0.7rem', color: 'text.secondary' }}>No assignments in this window.</Typography>
      )}
      {rows.length === 0 && (pinnedRows ?? []).length > 0 && (
        <Typography sx={{ px: 2, py: 0.75, fontSize: '0.66rem', color: 'text.secondary' }}>Nothing else on their plate in this window.</Typography>
      )}
      {rows.map((row, ri) => renderLine({
        key: row.key, nickname: row.nickname, taskName: row.taskName,
        chipColor: row.isAbsence ? s.absenceVacation : chipPalette[row.colorIdx % chipPalette.length],
        title: `${row.projectLabel} — takes ${row.runs[0]?.percent ?? 0}% of their day while it runs`,
        divider: (pinnedRows ?? []).length > 0 ? ri === 0 : (ri > 0 && rows[ri - 1].nickname !== row.nickname),
        runs: row.runs.map((run) => {
          const g = barGeometry(run.startDate, run.endDate);
          return {
            key: `${run.lastBar.assignmentKey}-${run.startDate}`, left: g.left, width: g.width, percent: run.percent,
            color: row.isAbsence ? s.absenceVacation : run.tentative ? s.tentative : s.taskAssignment,
            tooltip: `${row.taskName} · ${run.startDate} – ${run.endDate} · ${run.percent}% of their day${run.tentative ? ' · tentative' : ''}`,
            onClick: onBarClick
              ? (e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); onBarClick(run.lastBar, e.currentTarget); }
              : undefined,
          };
        }),
      }))}
    </Box>
  );
};
